import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { toast } from "react-toastify";

import Dashboard from "../../../common/components/DetailsDashboard/DetailsDashboard";
import Sidebar from "../../../common/components/Main Menu/MainMenu";
import Breadcrumb from "../../../common/components/NavigationBreadcrumb/Breadcrumb";
import Spinner from "../../../common/components/Spinner/Spinner";
import InsightPDFDownload from "../../../common/components/InsightPanel/InsightPDFDownload";
import Navbar from "../../../common/components/HeaderOpsPage/Navbar";
import FeedbackPopup from "../../../common/components/Popups/FeedbackPopup";
import InsightNavigator from "../../../common/components/InsightPanel/InsightNavigator";

import { decodeId } from "../../../common/Helper/encdec";
import { postFeedback } from "../../services/postFeedback";
import {
  resetDetailsData,
  updateFeedbackStatus,
  getOperationDataInit,
} from "../../../redux/slices/operationHomeSlice";

import "./DetailsScreen.css";

const DetailsScreen = () => {
  const { memberId, callId } = useParams();
  const dispatch = useDispatch();

  const decodedMemberId = decodeId(memberId);
  const decodedCallId = decodeId(callId);
  const decodeMemberIdSpace = decodedMemberId?.replace(/^(CC)(\d+)/i, "$1 $2");
  console.log(decodeMemberIdSpace, decodedCallId);

  // ---------------- STATE ----------------
  const [triggerDownload, setTriggerDownload] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(2);
  const [observationText, setObservationText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(decodedCallId);

  // ---------------- REDUX ----------------
  const loading = useSelector((state) => state.operationHome.loading);
  const tableData = useSelector((state) => state.operationHome.table.data);
  const pageSize = useSelector((state) => state.operationHome.table.pageSize);

  // Re-fetch table data if Redux was cleared by a page refresh
  useEffect(() => {
    if (tableData.length === 0) {
      dispatch(getOperationDataInit({ name: "last365Days", limit: pageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const feedbackOptions = [
    "All good",
    "Minor observation",
    "Critical issues observed",
    "Enhancement Suggestion",
  ];

  useEffect(() => {
    return () => {
      dispatch(resetDetailsData());
    };
  }, [dispatch]);

  // Logic to check if feedback was already given
  const existingRecord = useMemo(() => {
    return tableData.find(
      (item) =>
        item.memberId === decodeMemberIdSpace && item.callId === decodedCallId,
    );
  }, [tableData, decodeMemberIdSpace, decodedCallId]);

  console.log(existingRecord);

  const isFeedbackGiven = existingRecord?.FeedbackStatus === true;

  // ---------------- HANDLERS ----------------

  const handleDownloadClick = () => {
    // 1. Trigger the download component
    setTriggerDownload(true);

    // 2. Show the toast message
    toast.info("Download started", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      icon: "📄",
    });
  };

  const handleFeedbackClick = () => {
    setIsFeedbackOpen(true);
  };

  const handleSubmitFeedback = async () => {
    if (!observationText.trim()) {
      toast.warning("Please enter observation");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        callId: decodedCallId,
        customerId: decodeMemberIdSpace,
        feedback: feedbackOptions[selectedObservation - 1],
        observation: observationText,
      };

      await postFeedback(payload);

      dispatch(
        updateFeedbackStatus({
          memberId: decodeMemberIdSpace,
          callId: decodedCallId,
        }),
      );

      setIsFeedbackOpen(false);
      toast.success(
        isFeedbackGiven ? "Feedback updated!" : "Feedback submitted!",
        { icon: false },
      );
    } catch (error) {
      toast.error("❌ Failed to process feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: "Home", link: "/landing" },
    { label: "Ops Portal", link: "/operationHome", state: { from: "details" } },
    { label: "Insight Details" },
  ];

  return (
    <>
      {loading && <Spinner loading={loading} />}

      <div className="main details-page-main">
        <Navbar />
        <div className="App details-app">
          <div className="details-main">
            <div className="details-sidebar">
              {/* <Sidebar logoLink="/landing" /> */}
            </div>

            <div className="details-secondary">
              <div className="breadcrumb-download-container">
                <Breadcrumb
                  breadcrumbItems={breadcrumbItems}
                  backLink="/operationHome"
                  backLinkState={{ from: "details" }}
                />

               

                <div className="actions-group">
                  <div className="feedback-btn-wrapper">
                    {isFeedbackGiven && (
                      <div className="feedback-success-badge">✓</div>
                    )}
                    <span
                      className="action-btn feedback-hover"
                      onClick={handleFeedbackClick}
                    >
                      Product Feedback
                    </span>
                  </div>

                  {/* UPDATED: OnClick handler changed here */}
                  <span
                    className={`action-btn download-pdf-hover${isPdfGenerating ? " disabled" : ""}`}
                    onClick={!isPdfGenerating ? handleDownloadClick : undefined}
                    style={
                      isPdfGenerating
                        ? {
                            opacity: 0.6,
                            cursor: "not-allowed",
                            pointerEvents: "none",
                          }
                        : undefined
                    }
                  >
                    {isPdfGenerating ? "Generating..." : "Download PDF"}
                  </span>
                </div>
              </div>

               <div className="details-nav-center">
                  <InsightNavigator currentCallId={selectedCallId} disabled={isPdfGenerating} />
                </div>

              <Dashboard
                memberId={decodedMemberId}
                callId={decodedCallId}
                onCallChange={setSelectedCallId}
                navigationDisabled={isPdfGenerating}
              />

              {/* ---------------- FEEDBACK POPUP ---------------- */}
              <FeedbackPopup
                isOpen={isFeedbackOpen}
                onClose={() => setIsFeedbackOpen(false)}
                selectedObservation={selectedObservation}
                setSelectedObservation={setSelectedObservation}
                observationText={observationText}
                setObservationText={setObservationText}
                onSubmit={handleSubmitFeedback}
                isSubmitting={isSubmitting}
                isFeedbackGiven={isFeedbackGiven}
              />
            </div>
          </div>
        </div>
      </div>

      {triggerDownload && (
        <InsightPDFDownload
          triggerDownload={triggerDownload}
          setTriggerDownload={setTriggerDownload}
          memberId={decodedMemberId}
          callId={selectedCallId}
          onLoadingChange={setIsPdfGenerating}
        />
      )}
    </>
  );
};

export default DetailsScreen;
